import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLang } from '../context/LangContext';
import { useAuth } from '../context/AuthContext';
import { mockMaps } from '../data/mockMaps';
import InteractiveVotingMap from '../components/InteractiveVotingMap';
import SafeIcon from '../common/SafeIcon';
import { FiCamera, FiBarChart2, FiAlertCircle, FiShield } from 'react-icons/fi';
import { Skeleton } from '../components/Skeleton';
import IdentityModal from '../components/IdentityModal';
import OptionChoiceModal from '../components/OptionChoiceModal';
import toast from 'react-hot-toast';
import { cn } from '../utils/cn';
import { countries } from '../data/countries';
import { isoA2ToA3 } from '../utils/geoCodes';
import { detectCountry } from '../services/geo';
import {
  castVote,
  getVoteTally,
  getVoterMapVotes,
  applyTallyToMap,
  cacheLockedCountries,
  getDeviceId,
} from '../services/votes';

// --- Helper Components (Defined first to avoid hoisting errors) ---

const TallyRow = ({ option, votes, pct }) => (
  <div className="flex justify-between items-end">
    <div className="flex items-center gap-3">
      {option?.image && (
        <img
          src={option.image}
          alt={option.name}
          className="w-10 h-10 rounded-full border-2 object-cover"
          style={{ borderColor: option.color }}
        />
      )}
      <div>
        <div className="font-bold text-lg leading-none" style={{ color: option?.color }}>
          {option?.name || 'Loading...'}
        </div>
        <div className="text-slate-400 text-sm">{votes?.toLocaleString() || 0} votes</div>
      </div>
    </div>
    <div className="text-3xl font-black italic" style={{ color: option?.color }}>{pct}%</div>
  </div>
);

const ProgressBar = ({ pctA, pctB, colorA, colorB }) => (
  <div className="h-6 w-full bg-slate-800 rounded-full overflow-hidden flex shadow-inner mt-8">
    <motion.div
      initial={{ width: 0 }}
      animate={{ width: `${pctA}%` }}
      className="h-full"
      style={{ backgroundColor: colorA }}
    />
    <motion.div
      initial={{ width: 0 }}
      animate={{ width: `${pctB}%` }}
      className="h-full"
      style={{ backgroundColor: colorB }}
    />
  </div>
);

const DetailSkeleton = () => (
  <div className="min-h-screen bg-slate-950 p-4 md:p-8 max-w-7xl mx-auto space-y-8">
    <Skeleton className="h-12 w-1/2" />
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <Skeleton className="lg:col-span-2 h-[60vh]" />
      <div className="space-y-4">
        <Skeleton className="h-64" />
        <Skeleton className="h-16" />
      </div>
    </div>
  </div>
);

const NotFound = () => {
  const { t } = useLang();
  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center p-4">
      <SafeIcon icon={FiAlertCircle} className="text-6xl text-red-500 mb-6" />
      <h1 className="text-4xl font-black uppercase italic mb-4">{t('map_not_found')}</h1>
      <Link to="/" className="px-8 py-3 bg-lime-400 text-slate-950 font-bold rounded-full hover:bg-lime-300 transition-colors">
        {t('go_home')}
      </Link>
    </div>
  );
};

function toEntityCode(voteKey) {
  if (!voteKey) return null;
  if (voteKey.length === 2) return isoA2ToA3(voteKey) ?? voteKey;
  return voteKey;
}

function countryNameForCode(code) {
  return countries.find((c) => c.code === code)?.name ?? code;
}

function formatCountryLockedMessage(errorMessage) {
  const match = errorMessage?.match(/locked to\s+(.+)$/i);
  if (!match) return errorMessage;

  const codes = match[1].split(',').map((s) => s.trim()).filter(Boolean);
  if (codes.length >= 2) {
    return `You're locked to voting as ${countryNameForCode(codes[0])} and ${countryNameForCode(codes[1])}`;
  }
  return errorMessage;
}

function formatAlreadyVotedMessage(errorMessage, t) {
  const match = errorMessage?.match(/as\s+(\S+)\s*$/i);
  if (match) {
    return t('already_voted_as', countryNameForCode(match[1]));
  }
  return errorMessage;
}

// --- Main Page Component ---

export default function MapDetail() {
  const { id } = useParams();
  const { t } = useLang();
  const { lockedCountries, syncLockedCountriesFromVote } = useAuth();

  const [loading, setLoading] = useState(true);
  const [mapData, setMapData] = useState(null);
  const [pendingVoteGeo, setPendingVoteGeo] = useState(null);
  const [pendingIdentityCountry, setPendingIdentityCountry] = useState(null);
  const [readySecondIdentity, setReadySecondIdentity] = useState(null);
  const [detectedCountry, setDetectedCountry] = useState(null);
  const [isIdentityModalOpen, setIsIdentityModalOpen] = useState(false);
  const [isOptionModalOpen, setIsOptionModalOpen] = useState(false);
  const [submittingVote, setSubmittingVote] = useState(false);
  const [mapVotedCountries, setMapVotedCountries] = useState([]);

  const refreshTally = useCallback(async (baseMap) => {
    if (!baseMap?.supabaseId) return baseMap;
    const tally = await getVoteTally(baseMap.supabaseId);
    return applyTallyToMap(baseMap, tally);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadMap() {
      setLoading(true);
      const foundMap = mockMaps.find((m) => m.id === id);

      if (!foundMap) {
        if (!cancelled) {
          setMapData(null);
          setLoading(false);
        }
        return;
      }

      try {
        const withTally = foundMap.supabaseId
          ? await refreshTally(foundMap)
          : foundMap;
        if (!cancelled) setMapData(withTally);

        if (!cancelled && foundMap.supabaseId) {
          const voted = await getVoterMapVotes(foundMap.supabaseId);
          if (!cancelled) setMapVotedCountries(voted);
        }
      } catch (err) {
        console.error('Failed to load vote tally:', err);
        if (!cancelled) {
          setMapData({ ...foundMap, votesA: 0, votesB: 0, countryVotes: {} });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadMap();
    return () => { cancelled = true; };
  }, [id, refreshTally]);

  useEffect(() => {
    if (lockedCountries.length > 0) return;

    let cancelled = false;

    async function detect() {
      const country = await detectCountry();
      if (!cancelled && country) setDetectedCountry(country);
    }

    detect();
    return () => { cancelled = true; };
  }, [lockedCountries.length]);

  const resetPendingVote = () => {
    setPendingVoteGeo(null);
    setPendingIdentityCountry(null);
  };

  const openOptionModal = (geoId, identityCountry) => {
    const entityCode = toEntityCode(geoId);
    if (!entityCode) {
      toast.error('Invalid territory selected.');
      return false;
    }
    setPendingVoteGeo(geoId);
    setPendingIdentityCountry(identityCountry);
    setIsOptionModalOpen(true);
    return true;
  };

  const availableCountriesForMap = lockedCountries.filter(
    (code) => !mapVotedCountries.includes(code)
  );
  const mapVotesExhausted =
    lockedCountries.length >= 2 && availableCountriesForMap.length === 0;
  const singleRemainingCountry =
    lockedCountries.length >= 2 && availableCountriesForMap.length === 1
      ? availableCountriesForMap[0]
      : null;

  const handleVoteClick = (geoId) => {
    if (mapVotesExhausted) {
      toast.error(t('map_votes_exhausted'));
      return;
    }

    if (lockedCountries.length >= 2) {
      if (singleRemainingCountry) {
        openOptionModal(geoId, singleRemainingCountry);
        return;
      }
      setPendingVoteGeo(geoId);
      setIsIdentityModalOpen(true);
      return;
    }

    if (lockedCountries.length === 0) {
      if (!detectedCountry) {
        toast.error(t('country_detect_failed'));
        return;
      }
      openOptionModal(geoId, detectedCountry);
      return;
    }

    const soleLock = lockedCountries[0];
    if (mapVotedCountries.includes(soleLock)) {
      if (readySecondIdentity) {
        if (mapVotedCountries.includes(readySecondIdentity)) {
          toast.error(t('already_voted_as', countryNameForCode(readySecondIdentity)));
          return;
        }
        openOptionModal(geoId, readySecondIdentity);
        return;
      }
      setPendingVoteGeo(geoId);
      setIsIdentityModalOpen(true);
      return;
    }

    if (readySecondIdentity) {
      if (mapVotedCountries.includes(readySecondIdentity)) {
        openOptionModal(geoId, soleLock);
        return;
      }
      openOptionModal(geoId, readySecondIdentity);
      return;
    }

    openOptionModal(geoId, soleLock);
  };

  const handleUseSecondVote = () => {
    resetPendingVote();
    setIsIdentityModalOpen(true);
  };

  const handleIdentityConfirm = async (countryCode) => {
    if (mapVotedCountries.includes(countryCode)) {
      toast.error(t('already_voted_as', countryNameForCode(countryCode)));
      return false;
    }

    if (lockedCountries.length === 1 && lockedCountries.includes(countryCode)) {
      toast.error('Choose a different country for your 2nd vote.');
      return false;
    }

    if (lockedCountries.length === 1) {
      setReadySecondIdentity(countryCode);
    }

    if (pendingVoteGeo) {
      return openOptionModal(pendingVoteGeo, countryCode);
    }

    if (lockedCountries.length === 1) {
      toast.success(t('second_vote_click_map'));
    }
    return true;
  };

  const handleOptionConfirm = async (optionKey) => {
    if (!pendingVoteGeo || !pendingIdentityCountry || !mapData?.supabaseId) return;

    const entityCode = toEntityCode(pendingVoteGeo);
    if (!entityCode) {
      toast.error('Invalid territory selected.');
      return;
    }

    setSubmittingVote(true);
    try {
      const result = await castVote({
        votingMapId: mapData.supabaseId,
        entityCode,
        optionChoice: optionKey.toLowerCase(),
        country: pendingIdentityCountry,
        ipHash: getDeviceId(),
      });

      const locked = cacheLockedCountries(
        result.locked_country_1,
        result.locked_country_2
      );
      syncLockedCountriesFromVote(locked[0], locked[1]);

      if (Array.isArray(result.voted_countries_on_map)) {
        setMapVotedCountries(result.voted_countries_on_map);
      } else {
        const voted = await getVoterMapVotes(mapData.supabaseId);
        setMapVotedCountries(voted);
      }

      const updated = await refreshTally(mapData);
      setMapData(updated);
      toast.success(`${t('vote_success')} (Identity: ${pendingIdentityCountry})`);
      setIsOptionModalOpen(false);
      setReadySecondIdentity(null);
      resetPendingVote();
    } catch (err) {
      const message = err?.message ?? 'Vote failed';
      if (message.includes('COUNTRY_LOCKED')) {
        toast.error(formatCountryLockedMessage(message));
      } else if (message.includes('ALREADY_VOTED')) {
        toast.error(formatAlreadyVotedMessage(message, t));
      } else {
        toast.error(message);
      }
    } finally {
      setSubmittingVote(false);
    }
  };

  if (loading) return <DetailSkeleton />;
  if (!mapData) return <NotFound />;

  const totalVotes = mapData.votesA + mapData.votesB;
  const pctA = totalVotes > 0 ? Math.round((mapData.votesA / totalVotes) * 100) : 50;
  const pctB = 100 - pctA;
  const isFullyLocked = lockedCountries.length >= 2;
  const canUseSecondVote = lockedCountries.length === 1;

  const slotLabel = (index) => {
    if (lockedCountries[index]) return countryNameForCode(lockedCountries[index]);
    if (index === 0 && detectedCountry && lockedCountries.length === 0) {
      return countryNameForCode(detectedCountry);
    }
    if (index === 1 && readySecondIdentity && lockedCountries.length === 1) {
      return countryNameForCode(readySecondIdentity);
    }
    return 'Slot Empty';
  };

  const slotIsPending = (index) => {
    if (lockedCountries[index]) return false;
    if (index === 0 && detectedCountry && lockedCountries.length === 0) return true;
    if (index === 1 && readySecondIdentity && lockedCountries.length === 1) return true;
    return false;
  };

  return (
    <div className="min-h-screen bg-slate-950 p-4 md:p-8">
      <IdentityModal
        isOpen={isIdentityModalOpen}
        onClose={() => setIsIdentityModalOpen(false)}
        onCancel={() => {
          setIsIdentityModalOpen(false);
          resetPendingVote();
        }}
        onConfirm={handleIdentityConfirm}
        usedOnMapCountries={mapVotedCountries}
      />

      <OptionChoiceModal
        isOpen={isOptionModalOpen}
        onClose={() => {
          setIsOptionModalOpen(false);
          resetPendingVote();
        }}
        optionA={mapData.optionA}
        optionB={mapData.optionB}
        onConfirm={handleOptionConfirm}
        submitting={submittingVote}
      />

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 flex flex-col h-[60vh] lg:h-[80vh]">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-3xl md:text-5xl font-black uppercase italic tracking-tighter">
              {mapData.title}
            </h1>
            <span className="bg-slate-800 text-slate-300 px-3 py-1 rounded text-sm font-bold uppercase">
              {mapData.region}
            </span>
          </div>
          <div className="flex-1 relative rounded-xl shadow-[0_0_40px_rgba(0,0,0,0.5)] border border-slate-800">
            <InteractiveVotingMap
              mapData={mapData}
              onVoteCast={handleVoteClick}
              votingDisabled={mapVotesExhausted}
            />
          </div>
        </div>

        <div className="flex flex-col gap-6 lg:mt-16">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 p-2 opacity-10">
              <SafeIcon icon={FiBarChart2} className="text-6xl" />
            </div>
            <h3 className="text-xl font-bold mb-6 uppercase border-b border-slate-800 pb-2">{t('live_tally')}</h3>

            <div className="space-y-6">
              <TallyRow option={mapData.optionA} votes={mapData.votesA} pct={pctA} />
              <TallyRow option={mapData.optionB} votes={mapData.votesB} pct={pctB} />
            </div>

            <ProgressBar pctA={pctA} pctB={pctB} colorA={mapData.optionA.color} colorB={mapData.optionB.color} />

            <div className="mt-8 pt-4 border-t border-slate-800">
              <div className="flex items-center gap-2 mb-2">
                <SafeIcon icon={FiShield} className="text-lime-400 text-xs" />
                <span className="text-[10px] font-black uppercase text-slate-500 tracking-wider">
                  {t('identity_lock_status')}:
                </span>
              </div>
              <div className="flex gap-2 mb-3">
                {[0, 1].map(i => (
                  <div key={i} className={cn(
                    "flex-1 h-8 rounded border flex items-center justify-center text-[10px] font-bold uppercase tracking-tighter px-1",
                    lockedCountries[i]
                      ? "bg-lime-400/10 border-lime-400/30 text-lime-400"
                      : slotIsPending(i)
                        ? "bg-slate-800 border-slate-600 text-slate-300"
                        : "bg-slate-950 border-slate-800 text-slate-700"
                  )}>
                    {slotLabel(i)}
                  </div>
                ))}
              </div>

              <p className="text-center text-slate-500 text-[10px] mb-3">
                {t('change_country_signup')}
              </p>

              {canUseSecondVote && (
                <button
                  type="button"
                  onClick={handleUseSecondVote}
                  className="w-full py-3 mb-3 bg-lime-400/10 border border-lime-400/30 text-lime-400 font-black uppercase tracking-widest text-xs rounded-xl hover:bg-lime-400/20 transition-colors"
                >
                  {t('use_second_vote')}
                </button>
              )}

              {isFullyLocked && (
                <p className="text-center text-lime-400/80 text-[10px] font-bold mb-1">
                  {t('locked_to_voting', countryNameForCode(lockedCountries[0]), countryNameForCode(lockedCountries[1]))}
                </p>
              )}
              {mapVotesExhausted ? (
                <p className="text-center text-amber-400/90 text-xs font-bold">
                  {t('map_votes_exhausted')}
                </p>
              ) : (
                <p className="text-center text-slate-500 text-[10px]">
                  {t('click_territory_to_vote')}
                </p>
              )}
            </div>
          </div>

          <Link to={`/map/${mapData.id}/meme`} className="group relative flex items-center justify-center gap-3 w-full py-4 bg-gradient-to-r from-fuchsia-600 to-purple-600 rounded-xl font-black uppercase tracking-wider overflow-hidden hover:scale-[1.02] transition-transform shadow-[0_0_20px_rgba(192,38,211,0.3)]">
            <SafeIcon icon={FiCamera} className="text-2xl" />
            <span>{t('create_meme_btn')}</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
